import React, { useMemo, useState, useEffect } from 'react';
import {
  Title,
  Paper,
  Box,
  Loader,
  Alert,
  Tabs,
  Card,
  Text,
  Anchor,
  Center,
  Group,
  Stack,
  Switch,
  Button,
} from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { IconDownload } from '@tabler/icons-react';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../SQLEditorPopup';

const ColorLegend = () => (
  <Card shadow="sm" padding="xs" radius="sm" withBorder>
    <Title order={6} size="sm" mb={4}>
      Legend
    </Title>
    <Stack gap={4}>
      {[
        { color: 'var(--mantine-color-green-6)', label: 'Margin ≥ 20%' },
        { color: 'var(--mantine-color-yellow-6)', label: 'Margin between 15% and 20%' },
        { color: 'var(--mantine-color-red-6)', label: 'Margin < 15%' },
      ].map(({ color, label }) => (
        <Group key={label} gap={6} align="center">
          <Box
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: 2,
              flexShrink: 0,
            }}
          />
          <Text size="xs" c="dimmed">
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

function PurchasingDepartmentInventoryPlannedSpendDetails() {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const period = searchParams.get('period')?.toUpperCase() || 'MTD';
  const filterParam = searchParams.get('filter')?.toUpperCase();

  // Initialize state based on URL parameter, defaulting to true if no filter is specified
  const [onlyPurchasingWarehouses, setOnlyPurchasingWarehouses] = useState(() => {
    if (filterParam) {
      return filterParam === 'IN';
    }
    return true; // Default to true when no filter parameter is present
  });
  
  // State for pop-up dialogs
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);

  // Update state when URL changes
  useEffect(() => {
    const newFilterParam = searchParams.get('filter')?.toUpperCase();
    if (newFilterParam) {
      setOnlyPurchasingWarehouses(newFilterParam === 'IN');
    }
  }, [location.search]);

  // Generate the filter value based on checkbox state
  const warehouseFilter = onlyPurchasingWarehouses ? 'IN' : 'OUT';

  // Build URL with both period and filter parameters
  const url = `${config.apiBaseUrl}/supply-chain/purchasing/department/scorecard/inventory-planned-spend-details?period=${period}&filter=${warehouseFilter}`;
  const { data, isLoading, error } = useAuthenticatedData(url);

  const handleTabChange = (newPeriod) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('period', newPeriod);
    newSearchParams.set('filter', warehouseFilter);
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const handleFilterChange = (event) => {
    const newValue = event.currentTarget.checked;
    setOnlyPurchasingWarehouses(newValue);
    
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('filter', newValue ? 'IN' : 'OUT');
    
    navigate({
      pathname: location.pathname,
      search: newSearchParams.toString(),
    });
  };

  const rows = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index,
      // Extract values from the nested structure
      entered: item.values?.entered,
      partnumber: item.values?.partnumber,
      condition: item.values?.condition,
      accountName: item.values?.accountname,
      fullname: item.values?.fullname,
      qtyOrdered: item.values?.qtyordered,
      unitPrice: item.values?.unitprice,
      unitSellPrice: item.values?.unitsellprice,
      spend: item.values?.spend,
      projectedRev: item.values?.projectedrev,
      projectedMargin: item.values?.projectedmargin,
      marginPercent: item.values?.marginpercent,
      shipDate: item.values?.shipdate,
      dueDate: item.values?.duedate,
      docNo: item.values?.docno,
      userLine: item.values?.userline,
      docType: item.values?.doctype,
    }));
  }, [data]);

   // Calculate totals for relevant columns
  const totals = useMemo(() => {
    if (!rows.length) return {};
    
    return {
      spend: rows.reduce((sum, row) => sum + (Number(row.spend) || 0), 0),
      projectedRev: rows.reduce((sum, row) => sum + (Number(row.projectedRev) || 0), 0),
      projectedMargin: rows.reduce((sum, row) => sum + (Number(row.projectedMargin) || 0), 0),
    };
  }, [rows]);

  const generateExportFileName = () => `Purchasing-${period}-inventory-spend-details`;

  // CSV export configuration
  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
    filename: generateExportFileName(),
  });

  const handleExportData = () => {
    if (!rows.length) return;
    
    // Format data for export with proper column names
    const exportData = rows.map(row => ({
      'Entered': row.entered ? new Date(row.entered).toLocaleDateString() : '',
      'Part Number': row.partnumber || '',
      'Condition': row.condition || '',
      'Vendor': row.accountName || '',
      'Buyer': row.fullname || '',
      'Qty': row.qtyOrdered || '',
      'Unit Price': row.unitPrice || '',
      'Unit Sell Price': row.unitSellPrice || '',
      'Spend': row.spend || '',
      'Projected Revenue': row.projectedRev || '',
      'Projected Margin': row.projectedMargin || '',
      'Margin %': row.marginPercent ? `${Number(row.marginPercent).toFixed(2)}%` : '',
      'Ship Date': row.shipDate ? new Date(row.shipDate).toLocaleDateString() : '',
      'Due Date': row.dueDate ? new Date(row.dueDate).toLocaleDateString() : '',
      'Pentagon Doc#': row.docNo || '',
      'Line': row.userLine || '',
      'Doc Type': row.docType || '',
    }));

    const csv = generateCsv(csvConfig)(exportData);
    download(csvConfig)(csv);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'entered',
        header: 'Entered',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'partnumber', header: 'Part Number', size: 150 },
      { accessorKey: 'condition', header: 'Condition', size: 100 },
      { accessorKey: 'accountName', header: 'Vendor', size: 150 },
      { accessorKey: 'fullname', header: 'Buyer', size: 150 },
      { accessorKey: 'qtyOrdered', header: 'Qty', size: 80 },
      {
        accessorKey: 'unitPrice',
        header: 'Unit Price',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'unitSellPrice',
        header: 'Unit Sell Price',
        size: 140,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'spend',
        header: 'Spend',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedRev',
        header: 'Projected Revenue',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'projectedMargin',
        header: 'Projected Margin',
        size: 160,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toLocaleString()}` : '',
      },
      {
        accessorKey: 'marginPercent',
        header: 'Margin %',
        size: 120,
        Cell: ({ cell }) => cell.getValue() != null ? `${Number(cell.getValue()).toFixed(2)}%` : '',
      },
      {
        accessorKey: 'shipDate',
        header: 'Ship Date',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        size: 150,
        Cell: ({ cell }) => cell.getValue() ? new Date(cell.getValue()).toLocaleDateString() : '',
      },
      { accessorKey: 'docNo', header: 'Pentagon Doc#', size: 120 },
      { accessorKey: 'userLine', header: 'Line', size: 80 },
      { accessorKey: 'docType', header: 'Doc Type', size: 140 },
    ],
    []
  );

  const getRowStyle = (row) => {
    const margin = Number(row.original.marginPercent);
    if (isNaN(margin)) return {};

    if (margin >= 20) {
      return {
        backgroundColor: 'var(--mantine-color-green-1)',
        color: 'var(--mantine-color-green-9)',
      };
    }
    if (margin >= 15 && margin < 20) {
      return {
        backgroundColor: 'var(--mantine-color-yellow-1)',
        color: 'var(--mantine-color-yellow-9)',
      };
    }
    if (margin < 15) {
      return {
        backgroundColor: 'var(--mantine-color-red-1)',
        color: 'var(--mantine-color-red-9)',
      };
    }
    return {};
  };

  const table = useMantineReactTable({
    columns,
    data: rows,
    enableGlobalFilter: false,
    enableSorting: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enablePagination: true,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'xs',
      pagination: { pageSize: 25, pageIndex: 0 },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: getRowStyle(row),
    }),
    mantineTableProps: {
      style: { fontSize: '0.875rem' },
    },
    mantineBottomToolbarProps: {
      style: { padding: '8px' },
    },
    renderTopToolbarCustomActions: () => (
      <Box style={{ display: 'flex', gap: '8px', padding: '8px' }}>
        <Button
          onClick={handleExportData}
          leftSection={<IconDownload size={16} />}
          variant="filled"
          size="sm"
          disabled={rows.length === 0}
        >
          Export All Data
        </Button>
      </Box>
    ),
  });

  return (
    <Paper shadow="sm" style={{ maxWidth: '100%' }}>
      {/* Header with title and checkbox */}
      <Group justify="space-between" align="center" mb="md">
        <Text
          component="h1"
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
            marginBottom: 0,
            textAlign: 'left'
          }}
        >
          Purchasing Scorecard - Inventory Planned Spend Details
        </Text>
        
        <Switch
          label="Only purchasing warehouses"
          checked={onlyPurchasingWarehouses}
          onChange={handleFilterChange}
          size="sm"
        />
      </Group>

      {/* Technical options: small links for debugging */}
      <Group mb="sm" gap="sm">
     
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_dept_planned_inventory_spend_summary',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Summary Query
        </Anchor>
        <Anchor
          size="xs"
          c="dimmed"
          onClick={() =>
            window.open(
              '/query-commander/editor?queryKey=purchasing_dept_planned_inventory_spend_details',
              '_blank'
            )
          }
          style={{ cursor: 'pointer' }}
        >
          Details Query
        </Anchor>
      </Group>

      {/* About the Data and Legend Block */}
      <Group mb="sm" align="stretch" grow>
        <Card shadow="sm" padding="xs" radius="sm" withBorder>
          <Title order={6} size="sm" mb={4}>
            About the Data
          </Title>
          <Text size="xs" c="dimmed">
            These are purchase orders for parts to stock entered by the current purchasing team. You can see who's included by clicking the "Who is included?" link above. The price of the part as it existed in Pentagon at the time of the PO is used here - it doesn't matter what the sell price happens to be in Pentagon right now. The projected revenue is calculated by multiplying the quantity ordered by the projected sell price.
          </Text>
        </Card>
        <ColorLegend />
      </Group>

      <Box mt="sm">
        <Tabs value={period} onChange={handleTabChange} mb="sm">
          <Tabs.List>
            <Tabs.Tab value="MTD">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="QTD">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="YTD">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>
      </Box>

      <Paper shadow="sm" p="sm" style={{ height: 600 }}>
        <Box style={{ height: '100%' }}>
          {isLoading ? (
            <Center style={{ height: '100%' }}>
              <Group gap="sm">
                <Loader size="sm" />
                <Text size="sm">Loading...</Text>
              </Group>
            </Center>
          ) : error ? (
            <Alert color="red" title="Error">
              {error.message}
            </Alert>
          ) : (
            <MantineReactTable table={table} />
          )}
        </Box>
      </Paper>

 
    </Paper>
  );
}

export default PurchasingDepartmentInventoryPlannedSpendDetails;